import find from "lodash/find";
import size from "lodash/size";
import { CustomizationState } from "../../states/customization";
import { OptionsConfiguration } from "../../types/customization/configuration";
import { ContentEditablePicture, ContentEditableText } from "../../types/customization/content";
import { Content, ContentPage } from "../../types/customization/content";
import { Template, TemplatePageDefinition } from "../../types/customization/template";
import { findPhoto } from "../selection/finders";
import { sizeDmmToMm } from "./box";
import { evalContentCondition } from "./condition";
import { calcDefaultCropping, calcNextCropping, isZoomedOut } from "./cropping";
import {
  findEditablePictureDefinition,
  findEditableTextDefinition,
  findPageDefinition,
  findPageDefinitionsWithValidCondition,
  findPagesWithInvalidCondition,
} from "./finders";
import {
  buildRotationPagesMapping,
  calcEditablePicturesMapping,
  calcEditableTextsMapping,
  RotationPagesMappingCollection,
} from "./page";
import { countEditablePicturesInPage, layoutsByEditablePicturesCount } from "./template";

export function changeOnePagePageDefinition(
  page: ContentPage,
  draftState: CustomizationState,
  targetPageDefinitionKey: string,
  options: OptionsConfiguration[]
): { hasLoss: boolean } {
  const { template } = draftState;

  const currentPageDefinition = findPageDefinition(page.pageDefinitionKey, template);
  const targetPageDefinition = findPageDefinition(targetPageDefinitionKey, template);

  if (currentPageDefinition.filterType !== targetPageDefinition.filterType) {
    return { hasLoss: false };
  }

  if (!targetPageDefinition.backgroundColorKeys.includes(page.backgroundColorKey)) {
    page.backgroundColorKey = targetPageDefinition.backgroundColorKeys[0];
  }

  const { editablePictures, editableTexts } = page;
  page.editablePictures = [];
  page.editableTexts = [];
  page.pageDefinitionKey = targetPageDefinition.key;

  const editablePicturesMapping = calcEditablePicturesMapping(
    editablePictures,
    currentPageDefinition,
    targetPageDefinition
  );

  for (const mapping of editablePicturesMapping) {
    const { from: editablePictureKeyFrom, to: editablePictureKeyTo } = mapping;

    if (editablePictureKeyFrom !== null && editablePictureKeyTo !== null) {
      const editablePictureFrom = find(
        editablePictures,
        ({ editablePictureKey }) => editablePictureKey === editablePictureKeyFrom
      );

      if (editablePictureFrom) {
        const elementDefinitionFrom = findEditablePictureDefinition(
          editablePictureKeyFrom,
          currentPageDefinition
        );
        const elementFromSizeMm = sizeDmmToMm(elementDefinitionFrom);
        const ratioFrom = elementFromSizeMm.width.value / elementFromSizeMm.height.value;

        const elementDefinitionTo = findEditablePictureDefinition(
          editablePictureKeyTo,
          targetPageDefinition
        );
        if (
          elementDefinitionTo.conditions &&
          false ===
            evalContentCondition(
              elementDefinitionTo.conditions,
              draftState.template,
              draftState.content,
              options
            )
        ) {
          continue;
        }
        const elementToSizeMm = sizeDmmToMm(elementDefinitionTo);
        const ratioTo = elementToSizeMm.width.value / elementToSizeMm.height.value;

        const selectionPhotoFrom = findPhoto(
          editablePictureFrom.selectionPhotoKey,
          draftState.selection
        );

        const shouldResetCropping =
          !!elementDefinitionTo.shapeKey &&
          !elementDefinitionFrom.shapeKey &&
          isZoomedOut(editablePictureFrom);

        const croppingTo = shouldResetCropping
          ? calcDefaultCropping({ selectionPhoto: selectionPhotoFrom, nextRatio: ratioTo }, false)
          : calcNextCropping(
              {
                nextRatio: ratioTo,
                prevCropping: editablePictureFrom.cropping,
                prevRatio: ratioFrom,
              },
              !elementDefinitionTo.shapeKey
            );

        const newEditablePictureTo: ContentEditablePicture = {
          cropping: croppingTo,
          editablePictureKey: editablePictureKeyTo,
          filterTag: editablePictureFrom.filterTag,
          selectionPhotoKey: selectionPhotoFrom.key,
        };

        page.editablePictures.push(newEditablePictureTo);
      }
    } else if (editablePictureKeyTo === null) {
      // should add image to stash pile
      const editablePictureFrom = editablePictures.find(
        ({ editablePictureKey }) => editablePictureKey === editablePictureKeyFrom
      );
      const selectionPhoto =
        editablePictureFrom &&
        draftState.selection.photos.find(
          ({ key }) => editablePictureFrom.selectionPhotoKey === key
        );
      const stashedPictures = draftState.stash.selection.find(
        (element) => element.pageKey === page.key
      );
      if (stashedPictures && selectionPhoto && selectionPhoto.fotomKey) {
        stashedPictures.selectionPhotoKeys.push(selectionPhoto.fotomKey);
      } else if (selectionPhoto && selectionPhoto.fotomKey) {
        draftState.stash.selection.push({
          pageKey: page.key,
          selectionPhotoKeys: [selectionPhoto.fotomKey],
        });
      }
    } else if (editablePictureKeyFrom === null) {
      // should get image from stash pile
      const stashedPictures = draftState.stash.selection.find(
        (element) => element.pageKey === page.key
      );
      if (stashedPictures) {
        const selectionFotomKey = stashedPictures.selectionPhotoKeys.shift();
        const selectionPhoto = draftState.selection.photos.find(
          ({ fotomKey }) => !!fotomKey && fotomKey === selectionFotomKey
        );

        const elementDefinitionTo = findEditablePictureDefinition(
          editablePictureKeyTo,
          targetPageDefinition
        );
        const elementToSizeMm = sizeDmmToMm(elementDefinitionTo);
        const ratioTo = elementToSizeMm.width.value / elementToSizeMm.height.value;

        if (selectionPhoto) {
          const croppingTo = calcDefaultCropping(
            {
              nextRatio: ratioTo,
              selectionPhoto,
            },
            !elementDefinitionTo.shapeKey
          );

          const newEditablePictureTo: ContentEditablePicture = {
            cropping: croppingTo,
            editablePictureKey: editablePictureKeyTo,
            filterTag: "none",
            selectionPhotoKey: selectionPhoto.key,
          };

          page.editablePictures.push(newEditablePictureTo);
        }
      }
    }
  }

  let hasLoss = false;

  const editableTextsMapping = calcEditableTextsMapping(
    editableTexts,
    currentPageDefinition,
    targetPageDefinition
  );

  for (const mapping of editableTextsMapping) {
    const { from: editableTextKeyFrom, to: editableTextKeyTo } = mapping;

    const editableTextFrom = find(
      editableTexts,
      ({ editableTextKey }) => editableTextKey === editableTextKeyFrom
    );

    if (editableTextFrom) {
      const contentFrom = editableTextFrom.content;

      if (editableTextKeyTo === null) {
        if (contentFrom) {
          hasLoss = true;
        }
      } else {
        const fontKeyFrom = editableTextFrom.fontKey;

        const elementDefinitionTo = findEditableTextDefinition(
          editableTextKeyTo,
          targetPageDefinition
        );
        if (
          elementDefinitionTo.conditions &&
          false ===
            evalContentCondition(
              elementDefinitionTo.conditions,
              draftState.template,
              draftState.content
            )
        ) {
          continue;
        }

        const newEditableTextTo: ContentEditableText = {
          content: contentFrom,
          editableTextKey: editableTextKeyTo,
          fontKey: fontKeyFrom,
        };

        page.editableTexts.push(newEditableTextTo);
      }
    }
  }

  if (
    draftState.content.templateType === "book" ||
    (draftState.content.templateType === "calendar" && draftState.content.titleFontKey)
  ) {
    let newFontKey = draftState.content.titleFontKey;

    for (const elementDefinition of targetPageDefinition.elementDefinitions) {
      if (
        elementDefinition.type === "forced-text" &&
        elementDefinition.forcedType === "interpolated-content" &&
        elementDefinition.interpolatedContent === "title"
      ) {
        newFontKey = elementDefinition.fontKey;
      }
    }
    draftState.content.titleFontKey = newFontKey;
  }

  return { hasLoss };
}

export function selectablePageDefinitionsCount(
  template: Template,
  content: Content,
  useType?: { type?: string; filterType?: string }
): number {
  const pageDefinitions = findPageDefinitionsWithValidCondition(template, content, {
    type: useType?.type,
    filterType: useType?.filterType,
  });
  return pageDefinitions.length;
}

export function canSelectPageDefinitions(
  template: Template,
  content: Content,
  useType?: { type?: string; filterType?: string }
): boolean {
  return selectablePageDefinitionsCount(template, content, useType) > 1;
}

export function canSelectPageDefinitionsWithRotatedPairs(
  template: Template,
  rotationPagesMapping: RotationPagesMappingCollection,
  content: Content
): boolean {
  const rotationPagesCount = size(rotationPagesMapping);
  return (
    selectablePageDefinitionsCount(template, content) > 1 &&
    (rotationPagesCount === 0 || rotationPagesCount > 2)
  );
}

export function canSelectPageDefinitionsForOnePage(
  template: Template,
  content: Content,
  pageDefinition: TemplatePageDefinition
): boolean {
  const similarPageDefinitions = findPageDefinitionsWithValidCondition(template, content, {
    type: pageDefinition.type,
    filterType: pageDefinition.filterType,
  });
  const rotationPairsMapping = buildRotationPagesMapping(similarPageDefinitions);

  return (
    similarPageDefinitions.length > 2 ||
    (similarPageDefinitions.length === 2 && Object.keys(rotationPairsMapping).length === 0)
  );
}

export function checkAndReplaceInvalidPageDefinitions(
  draftState: CustomizationState,
  options: OptionsConfiguration[]
): void {
  const { content, template } = draftState;
  const invalidPages = findPagesWithInvalidCondition(template, content, options) || [];

  for (const invalidPage of invalidPages) {
    const invalidPageDefinition = findPageDefinition(invalidPage.pageDefinitionKey, template);
    const validPageDefinitions = findPageDefinitionsWithValidCondition(
      template,
      content,
      { type: invalidPageDefinition.type, filterType: invalidPageDefinition.filterType },
      options
    );
    if (validPageDefinitions.length === 0) {
      continue;
    }

    let newPageDefinitionKey = validPageDefinitions[0].key;

    const noEditableItemsPageTypes = [
      "back-cover",
      "layflat-back-matter",
      "layflat-front-matter",
      "single-back-matter",
      "single-front-matter",
      "spine",
    ];
    if (noEditableItemsPageTypes.includes(invalidPageDefinition.type) === false) {
      const matchingLayouts = layoutsByEditablePicturesCount(
        invalidPage.pageDefinitionKey,
        validPageDefinitions
      );
      const editablePicturesCount = countEditablePicturesInPage(invalidPageDefinition);
      if (matchingLayouts !== null && matchingLayouts[editablePicturesCount]) {
        newPageDefinitionKey = matchingLayouts[editablePicturesCount][0].key;
      }
    }
    changeOnePagePageDefinition(invalidPage, draftState, newPageDefinitionKey, options);
  }
}
